/**
 * DecisionCards — the "Braucht deinen Entscheid" KPI card row (Story UI-U3, AC 2).
 * Ported from the reference prototype's heute-view PRIORITIES cards.
 *
 * Each card: status = dot + colored text label (NEVER a filled pill), a big bold
 * tabular-mono focus value, a muted subline with a hover-arrow, and it navigates
 * to its target route on click (whole card is the button).
 *
 * Empty (live mode — no endpoint): a single design-conform empty state.
 *
 * [Source: brokly-design-reference/components/brokly/heute-view.tsx]
 * [Source: docs/design/2026-07-10_sebastian-package/design.md §2/§5]
 */
import { ArrowRight } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { Card } from '../../../components/ui/Card';
import { StatusDot } from '../../../components/ui/StatusDot';
import type { HeuteDecision } from '../heute-mock';

export function DecisionCards({ decisions }: { decisions: HeuteDecision[] }) {
  if (decisions.length === 0) {
    return (
      <Card className="p-8 text-center text-sm text-muted-foreground">
        Aktuell keine offenen Entscheide.
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {decisions.map((d, i) => {
        const Icon = d.icon;
        return (
          <Link
            key={i}
            to={d.to}
            className="group block rounded-xl border border-border bg-card p-4 shadow-xs transition-colors hover:border-ring/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-medium">
                <StatusDot tone={d.tone} label={d.label} />
              </span>
              <Icon className="size-4 text-muted-foreground" />
            </div>
            <div className="text-2xl font-bold mono-num text-foreground">{d.value}</div>
            <div className="mt-0.5 flex items-center gap-1 truncate text-xs text-muted-foreground">
              {d.sub}
              <ArrowRight className="size-3 -translate-x-1 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
            </div>
          </Link>
        );
      })}
    </div>
  );
}
